type ToNumberOptions = {
  /**
   * The value to return if the conversion fails.
   *
   * @defaultValue 0
   */
  fallback?: number;
};

const DEFAULT_OPTIONS = {
  fallback: 0,
} satisfies ToNumberOptions;

/**
 * Converts an input value to a number.
 *
 * @remarks
 * This utility ensures a finite number is always returned by handling common edge cases:
 * - Returns the `fallback` for `null`, `undefined`, or empty strings (`''`).
 * - Parses strings using {@link Number.parseFloat} and returns the `fallback` if the result is `NaN` or `Infinity`.
 *
 * @example
 * ```ts
 * toNumber('12.5'); // returns 12.5
 * toNumber('abc'); // returns 0
 * toNumber(null); // returns 0
 * toNumber(null, { fallback: -1 }); // returns -1
 * ```
 *
 * @param value - The value to convert.
 * @param options - Optional configuration object {@link ToNumberOptions}.
 *
 * @returns The parsed finite number, or the fallback value.
 */
export const toNumber = (value: number | null | string | undefined, options?: ToNumberOptions) => {
  if (value === null || value === undefined || value === '') {
    return options?.fallback ?? DEFAULT_OPTIONS.fallback;
  }

  const normalisedValue = typeof value === 'number' ? value : Number.parseFloat(value);

  if (!Number.isFinite(normalisedValue)) {
    return options?.fallback ?? DEFAULT_OPTIONS.fallback;
  }

  return normalisedValue;
};
